import React, { useState, useEffect, useCallback } from 'react';
import { accountingAPI } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { PageHeader } from '../../../components/layout/PageHeader';
import { Modal }      from '../../../components/common/Modal';
import { Table }      from '../../../components/common/Table';
import { Badge }      from '../../../components/common/Badge';
import { Pagination } from '../../../components/common/Pagination';
import { AccountingForm }       from '../accounting/AccountingForm';
import { InvoiceDownloadModal } from '../accounting/InvoicePDF';
import {
  PlusIcon, MagnifyingGlassIcon, ArrowDownTrayIcon,
  DocumentTextIcon, ClipboardDocumentListIcon,
  TruckIcon, ArrowUturnLeftIcon, CurrencyRupeeIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

/* Which invoice types a contractor can create */
const CONTRACTOR_TYPES = [
  { type: 'sale',             label: 'Tax Invoice',          icon: CurrencyRupeeIcon,        color: 'bg-green-100 text-green-700',  desc: 'Raise a tax invoice for completed work' },
  { type: 'quotation',        label: 'Quotation',            icon: ClipboardDocumentListIcon, color: 'bg-violet-100 text-violet-700', desc: 'Send a price quotation for upcoming work' },
  { type: 'proforma',         label: 'Proforma Invoice',     icon: DocumentTextIcon,          color: 'bg-blue-100 text-blue-700',    desc: 'Pre-invoice before formal billing' },
  { type: 'delivery_challan', label: 'Delivery Challan',     icon: TruckIcon,                 color: 'bg-cyan-100 text-cyan-700',    desc: 'Document for goods dispatched' },
  { type: 'debit_note',       label: 'Debit Note',           icon: ArrowUturnLeftIcon,        color: 'bg-red-100 text-red-700',      desc: 'Raise a debit note for returns or adjustments' },
];

const TYPE_LABEL = {
  sale: 'Tax Invoice', purchase: 'Purchase', expense: 'Expense',
  quotation: 'Quotation', indirect_income: 'Income',
  proforma: 'Proforma Invoice', delivery_challan: 'Delivery Challan', debit_note: 'Debit Note',
};

export function ContractorPortal() {
  const { user } = useAuth();
  const [records,    setRecords]    = useState([]);
  const [pagination, setPg]         = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page,       setPage]       = useState(1);
  const [formOpen,   setFormOpen]   = useState(false);
  const [formType,   setFormType]   = useState('sale');
  const [editing,    setEditing]    = useState(null);
  const [invoiceRec, setInvoiceRec] = useState(null);
  const [invoiceOpen,setInvoiceOpen]= useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await accountingAPI.getAll({
        type: typeFilter || undefined, search: search || undefined,
        contact: user?._id, page, limit: 15,
      });
      setRecords(res.data || []);
      setPg(res.pagination);
    } catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  }, [typeFilter, search, page, user]);

  useEffect(() => { load(); }, [load]);

  const openCreate = type => { setFormType(type); setEditing(null); setFormOpen(true); };
  const openEdit   = r    => { setEditing(r);     setFormType(r.type); setFormOpen(true); };

  const openInvoice = async r => {
    try { const res = await accountingAPI.getById(r._id); setInvoiceRec(res.data); setInvoiceOpen(true); }
    catch { toast.error('Failed to load invoice'); }
  };

  const handleSave = async formData => {
    try {
      editing
        ? await accountingAPI.update(editing._id, formData)
        : await accountingAPI.create({ ...formData, type: formType });
      toast.success(editing ? 'Invoice updated' : 'Invoice created');
      setFormOpen(false);
      load();
    } catch (err) { toast.error(err.message || 'Failed to save'); }
  };

  const cols = [
    { header: 'Ref #', render: r => (
      <span className="text-xs font-mono font-semibold text-indigo-600">{r.referenceNo}</span>
    )},
    { header: 'Type', render: r => (
      <span className="text-xs font-medium text-gray-600">{TYPE_LABEL[r.type] || r.type}</span>
    )},
    { header: 'Date', render: r => (
      <span className="text-sm text-gray-600">{format(new Date(r.date), 'dd MMM yyyy')}</span>
    )},
    { header: 'Amount', render: r => (
      <span className="font-bold text-gray-900 text-sm">{'₹' + Number(r.total || 0).toLocaleString('en-IN')}</span>
    )},
    { header: 'Status', render: r => <Badge status={r.status} /> },
    { header: '', render: r => (
      <button onClick={() => openInvoice(r)} title="Download Invoice"
        className="p-1.5 hover:bg-green-50 rounded-lg transition-colors">
        <ArrowDownTrayIcon className="w-4 h-4 text-green-600" />
      </button>
    )},
  ];

  /* Stats from current page */
  const stats = {
    total:   records.length,
    pending: records.filter(r => ['draft','pending'].includes(r.status)).length,
    paid:    records.filter(r => r.status === 'paid').length,
    value:   records.reduce((s,r) => s + Number(r.total||0), 0),
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Welcome, ${user?.name || 'Contractor'}`}
        subtitle="Manage your invoices and documents from your contractor portal"
      />

      {/* Quick Create Cards */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Create New Document</h2>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {CONTRACTOR_TYPES.map(({ type, label, icon: Icon, color, desc }) => (
            <button key={type} onClick={() => openCreate(type)}
              className="bg-white border border-gray-100 rounded-2xl p-4 text-left hover:border-indigo-300 hover:shadow-md transition-all group">
              <div className={`w-9 h-9 ${color} rounded-xl flex items-center justify-center mb-3`}>
                <Icon className="w-4 h-4" />
              </div>
              <p className="text-sm font-semibold text-gray-800 group-hover:text-indigo-700 leading-tight">{label}</p>
              <p className="text-xs text-gray-400 mt-1 leading-tight">{desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Docs',   value: pagination?.total || stats.total, color: 'text-indigo-600' },
          { label: 'Pending',      value: stats.pending,   color: 'text-amber-600'  },
          { label: 'Paid',         value: stats.paid,      color: 'text-green-600'  },
          { label: 'Total Value',  value: '₹' + (stats.value / 1000).toFixed(0) + 'K', color: 'text-indigo-600' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${s.color}`}>{loading ? '...' : s.value}</p>
          </div>
        ))}
      </div>

      {/* Invoice List */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex gap-3 flex-wrap items-center justify-between">
          <h3 className="font-semibold text-gray-800 text-sm">My Invoices & Documents</h3>
          <div className="flex gap-3 flex-1 flex-wrap justify-end">
            <div className="relative min-w-44">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search ref # ..."
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
              <option value="">All Types</option>
              {CONTRACTOR_TYPES.map(t => <option key={t.type} value={t.type}>{t.label}</option>)}
            </select>
          </div>
        </div>
        <Table columns={cols} data={records} loading={loading} emptyMessage="No documents found" />
        <Pagination pagination={pagination} onPageChange={setPage} />
      </div>

      {/* Create/Edit Form */}
      <AccountingForm isOpen={formOpen} onClose={() => setFormOpen(false)} onSave={handleSave} editing={editing} defaultType={formType} />

      {/* Invoice Preview */}
      <InvoiceDownloadModal isOpen={invoiceOpen} onClose={() => setInvoiceOpen(false)} record={invoiceRec} />
    </div>
  );
}